import React, { Component } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router-dom';

var animCSS = '\n\
@keyframes tos-fade-up {\n\
  from { opacity: 0; transform: translateY(24px); }\n\
  to { opacity: 1; transform: translateY(0); }\n\
}\n\
@keyframes tos-scale-in {\n\
  from { opacity: 0; transform: scale(0.85); }\n\
  to { opacity: 1; transform: scale(1); }\n\
}\n\
.tos-hidden { opacity: 0; }\n\
.tos-fade-up { animation: tos-fade-up 0.6s ease-out forwards; }\n\
.tos-scale-in { animation: tos-scale-in 0.5s ease-out forwards; }\n\
.tos-stagger-1 { animation-delay: 0s; }\n\
.tos-stagger-2 { animation-delay: 0.12s; }\n\
.tos-stagger-3 { animation-delay: 0.24s; }\n\
.tos-stagger-4 { animation-delay: 0.36s; }\n\
.tos-stagger-5 { animation-delay: 0.48s; }\n\
';

function Section(props) {
  var staggerClass = props.stagger ? ' tos-stagger-' + props.stagger : '';
  return (
    <div
      className={'tos-hidden tw-bg-white dark:tw-bg-[#141414] tw-rounded-2xl tw-border tw-border-[#e8e8e8] dark:tw-border-[#2a2a2a] tw-p-6 tw-mb-6' + staggerClass}
      style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
      data-anim="tos-fade-up"
    >
      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4">
        <div
          className="tw-w-10 tw-h-10 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-lg tw-flex-shrink-0"
          style={{ backgroundColor: props.iconBg || 'rgba(201,168,76,0.1)', color: props.iconColor || '#c9a84c' }}
        >
          <i className={'fa fa-' + props.icon} />
        </div>
        <h2 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mt-0 tw-mb-0">
          {props.title}
        </h2>
      </div>
      <div className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-leading-relaxed">
        {props.children}
      </div>
    </div>
  );
}

class TermsOfService extends Component {
  constructor(props) {
    super(props);
    this._observer = null;
    this.state = { showScrollTop: false };
    this._handleScroll = this._handleScroll.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this._setupObserver();
    window.addEventListener('scroll', this._handleScroll);
  }

  componentWillUnmount() {
    if (this._observer) {
      this._observer.disconnect();
    }
    window.removeEventListener('scroll', this._handleScroll);
  }

  _handleScroll() {
    var show = window.scrollY > 400;
    if (show !== this.state.showScrollTop) {
      this.setState({ showScrollTop: show });
    }
  }

  _setupObserver() {
    if (typeof IntersectionObserver === 'undefined') {
      var els = document.querySelectorAll('.tos-hidden');
      for (var i = 0; i < els.length; i++) {
        els[i].classList.remove('tos-hidden');
      }
      return;
    }

    var obs;
    obs = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var el = entry.target;
            var anim = el.getAttribute('data-anim') || 'tos-fade-up';
            el.classList.add(anim);
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -20px 0px' }
    );
    this._observer = obs;

    var self = this;
    setTimeout(function () {
      var els = document.querySelectorAll('.tos-hidden');
      for (var i = 0; i < els.length; i++) {
        self._observer.observe(els[i]);
      }
    }, 50);
  }

  render() {
    return (
      <div className="tw-min-h-screen">
        <style>{animCSS}</style>
        <Helmet>
          <title>Terms of Service | Primal Marketplace</title>
          <meta name="description" content="Terms of Service for Primal Marketplace, a platform for buying and selling Primal TCG trading cards." />
          <meta property="og:title" content="Terms of Service | Primal Marketplace" />
          <meta property="og:description" content="Read the Terms of Service for Primal Marketplace." />
        </Helmet>

        {/* Hero */}
        <div className="tw-text-center tw-pt-10 tw-pb-8 tw-px-4 tw-max-w-2xl tw-mx-auto" style={{ animation: 'tos-fade-up 0.6s ease-out both' }}>
          <h1 className="tw-text-3xl md:tw-text-4xl tw-font-extrabold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-4 tw-leading-tight">
            Terms of Service
          </h1>
          <p className="tw-text-lg tw-text-[#666] dark:tw-text-gray-400 tw-leading-relaxed tw-mb-0">
            Please read these terms carefully before using Primal Marketplace.
          </p>
          <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-mt-3">
            Last updated: February 2026
          </p>
        </div>

        <div className="tw-max-w-3xl tw-mx-auto tw-px-4 tw-pb-16">

          {/* 1. Acceptance */}
          <Section icon="check-circle" title="1. Acceptance of Terms" stagger="1">
            <p className="tw-mb-2">
              By accessing or using Primal Marketplace ("the Platform"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Platform.
            </p>
            <p className="tw-mb-0">
              We may update these terms from time to time. Continued use after changes constitutes acceptance of the revised terms. We will notify users of material changes via email or a prominent notice on the Platform.
            </p>
          </Section>

          {/* 2. Eligibility */}
          <Section icon="user" title="2. Eligibility" stagger="2">
            <p className="tw-mb-2">
              You must be at least <strong className="tw-text-[#1a1a1a] dark:tw-text-white">13 years old</strong> to use the Platform. Users under 18 may browse but may not engage in financial transactions (buying, selling, or receiving payouts) without parental consent.
            </p>
            <p className="tw-mb-0">
              By creating an account or making a purchase, you represent that you meet these age requirements and have the legal capacity to enter into a binding agreement.
            </p>
          </Section>

          {/* 3. Account Responsibilities */}
          <Section icon="lock" title="3. Account Responsibilities" stagger="3">
            <ul className="tw-list-disc tw-pl-5 tw-space-y-2 tw-mb-0">
              <li>You must provide accurate and complete information when creating your account.</li>
              <li>You are responsible for maintaining the security of your password and account.</li>
              <li>One account per person. Multiple accounts are not permitted.</li>
              <li>You must notify us immediately of any unauthorized use of your account.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
            </ul>
          </Section>

          {/* 4. Marketplace Rules */}
          <Section icon="shopping-bag" title="4. Marketplace Rules" stagger="4">
            <p className="tw-mb-3">
              Primal Marketplace connects buyers and sellers of Primal TCG trading cards. By using the Platform:
            </p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-2 tw-mb-3">
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Sellers</strong> must list only authentic Primal TCG cards. Counterfeit, misrepresented, or non-Primal TCG items are prohibited.</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Buyers</strong> are responsible for reviewing listings carefully before purchasing.</li>
              <li>All listings must include accurate descriptions, fair pricing, and clear images.</li>
              <li>Sellers must maintain their shop and respond to orders in a timely manner.</li>
            </ul>
            <p className="tw-mb-0">
              The Platform reserves the right to remove listings that violate these rules.
            </p>
          </Section>

          {/* 5. Fees & Payments */}
          <Section icon="credit-card" title="5. Fees & Payments" stagger="5">
            <p className="tw-mb-3">
              All transactions are processed in US Dollars (USD).
            </p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-2 tw-mb-3">
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Platform Commission:</strong> Primal Marketplace retains a 10% commission on each sale. Sellers receive 90% of the item price plus the full shipping amount.</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Shipping Fees:</strong> Set by the seller. Buyers pay shipping once per seller per order — additional items from the same seller ship free.</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Payment Methods:</strong> We accept Stripe (credit/debit cards) and PayPal.</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Platform Fee:</strong> Sellers keep 90% of the item price plus all shipping. On orders under $9, seller payouts may be slightly lower to cover payment processing costs.</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Make an Offer:</strong> Sellers can enable offers on their listings. Buyers may submit offers that expire after 48 hours. Offers must be at least 20% of the listing price. Both parties may counter-offer; counters must move toward a mutually agreeable price (sellers counter higher, buyers counter lower). Accepted offers are binding — the buyer must complete payment at the agreed price.</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Referral Program:</strong> Users can share listings via unique referral links and earn a flat dollar commission (minimum $1.00) on resulting sales. Commission is set by the seller and deducted from their earnings. Commissions are credited to a balance that can be withdrawn at any time (a $0.25 withdrawal fee applies).</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Buylist:</strong> Buyers can post wanted cards with target prices. Sellers may fulfill buylist requests, at which point standard transaction and escrow rules apply.</li>
            </ul>
            <p className="tw-mb-0">
              Prices are set by sellers. Primal Marketplace does not control pricing and is not responsible for pricing disputes between buyers and sellers.
            </p>
          </Section>

          {/* 6. Shipping & Delivery */}
          <Section icon="truck" title="6. Shipping & Delivery">
            <ul className="tw-list-disc tw-pl-5 tw-space-y-2 tw-mb-3">
              <li>Sellers must ship orders within <strong className="tw-text-[#1a1a1a] dark:tw-text-white">3 business days</strong> of purchase.</li>
              <li>Sellers must provide valid tracking information when shipping.</li>
              <li>Cards must be packaged securely following our <Link to="/shipping" className="tw-text-brand-gold hover:tw-underline">Shipping Guide</Link>.</li>
              <li>Risk of loss or damage transfers to the buyer once the carrier accepts the package.</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Shipping costs:</strong> Orders of $10 or more qualify for free shipping. Orders of $50 or more require tracked shipping (included automatically). Orders under $10 have a flat $1.00 shipping fee. An optional tracking upgrade is available for $5.50 on orders under $50.</li>
            </ul>
            <p className="tw-mb-0">
              Primal Marketplace is not responsible for delays caused by shipping carriers, weather, or customs.
            </p>
          </Section>

          {/* 7. Buyer Protection & Disputes */}
          <Section icon="shield" title="7. Buyer Protection & Disputes">
            <p className="tw-mb-3">
              Primal Marketplace uses an escrow model to protect buyers:
            </p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-2 tw-mb-3">
              <li>Payment is held by the Platform until delivery is confirmed.</li>
              <li>After the seller provides tracking, a <strong className="tw-text-[#1a1a1a] dark:tw-text-white">14-day hold period</strong> begins.</li>
              <li>During this period, the buyer may confirm receipt or open a dispute.</li>
              <li>If no action is taken, funds are automatically released to the seller after 14 days.</li>
              <li>Disputes are reviewed by our team and resolved within 3-5 business days.</li>
            </ul>
            <p className="tw-mb-2">
              Refund eligibility depends on the circumstances of the dispute (item not received, item not as described, damaged in transit, etc.).
            </p>
            <p className="tw-mb-0">
              Refunds are issued via the original payment method (Stripe or PayPal). Stripe refunds typically appear within 5-10 business days. PayPal refunds may take an additional 5-10 business days.
            </p>
          </Section>

          {/* 8. Prohibited Conduct */}
          <Section icon="ban" title="8. Prohibited Conduct">
            <p className="tw-mb-3">The following conduct is prohibited on the Platform:</p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-2 tw-mb-0">
              <li>Selling counterfeit, stolen, or misrepresented cards</li>
              <li>Shill bidding, price manipulation, or artificial inflation of engagement metrics</li>
              <li>Harassment, threats, or abusive communication toward other users</li>
              <li>Creating multiple accounts to circumvent bans or restrictions</li>
              <li>Attempting to complete transactions outside the Platform to avoid fees</li>
              <li>Using the Platform for money laundering, fraud, or any illegal activity</li>
              <li>Infringing on intellectual property rights of others</li>
              <li>Interfering with the operation or security of the Platform</li>
              <li>Sending unwanted or repeated messages after being blocked by another user</li>
            </ul>
          </Section>

          {/* 9. Intellectual Property */}
          <Section icon="copyright" title="9. Intellectual Property">
            <p className="tw-mb-2">
              Primal TCG cards, artwork, and game content are the intellectual property of their respective owners. Primal Marketplace does not claim ownership of card designs or game content.
            </p>
            <p className="tw-mb-0">
              By posting content on the Platform (listings, images, reviews), you grant Primal Marketplace a non-exclusive, worldwide license to display, distribute, and promote that content in connection with the Platform's services.
            </p>
          </Section>

          {/* 10. Data Collection & Monitoring */}
          <Section icon="bar-chart" title="10. Data Collection & Monitoring">
            <p className="tw-mb-2">
              By using the Platform, you acknowledge that we collect and process certain data to operate, improve, and secure the Platform. This includes:
            </p>
            <ul className="tw-list-disc tw-pl-5 tw-space-y-1 tw-mb-3">
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Error monitoring (Sentry):</strong> We automatically collect error reports, browser type, device information, and IP addresses when errors occur. This data helps us identify and fix bugs to maintain platform reliability. No personal content such as messages or payment details is captured.</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Analytics (Mixpanel):</strong> With your consent, we collect anonymized usage data including page views and feature interactions. You can opt out at any time via the cookie consent banner.</li>
              <li><strong className="tw-text-[#1a1a1a] dark:tw-text-white">Transactional emails (AWS SES):</strong> Your email address is used to send order confirmations, shipping updates, and account notifications.</li>
            </ul>
            <p className="tw-mb-0">
              For full details on how we handle your data, please review our <Link to="/privacy" className="tw-text-brand-gold hover:tw-underline">Privacy Policy</Link>.
            </p>
          </Section>

          {/* 11. Account Termination */}
          <Section icon="times-circle" title="11. Account Termination">
            <p className="tw-mb-2">
              Primal Marketplace may suspend or terminate your account at any time for violations of these Terms, fraudulent activity, or conduct that harms other users or the Platform.
            </p>
            <p className="tw-mb-2">
              <strong className="tw-text-[#1a1a1a] dark:tw-text-white">Strikes:</strong> Policy violations result in strikes on your account. Examples include listing counterfeit items, harassment, or having a listing removed by an administrator.
            </p>
            <p className="tw-mb-2">
              <strong className="tw-text-[#1a1a1a] dark:tw-text-white">Suspensions:</strong> Accumulating 3 strikes triggers an automatic 7-day suspension. Suspended accounts cannot buy, sell, or message. We may also issue manual suspensions for severe violations.
            </p>
            <p className="tw-mb-2">
              <strong className="tw-text-[#1a1a1a] dark:tw-text-white">Bans:</strong> Accounts may be permanently banned for fraud, repeated serious violations, or illegal activity. Banned accounts cannot be recovered.
            </p>
            <p className="tw-mb-2">
              <strong className="tw-text-[#1a1a1a] dark:tw-text-white">Appeals:</strong> You may appeal strikes through your Account Standing page. Appeals are reviewed by our team within 5 business days. Approved appeals remove the strike; denied appeals include an explanation.
            </p>
            <p className="tw-mb-2">
              <strong className="tw-text-[#1a1a1a] dark:tw-text-white">Blocking & Reporting:</strong> Users can block other users to prevent messaging and report users for policy violations. Reports are reviewed by our moderation team.
            </p>
            <p className="tw-mb-2">
              You may delete your account at any time from your <Link to="/settings" className="tw-text-brand-gold hover:tw-underline">account settings</Link>. Upon deletion, your personal information will be scrubbed in accordance with our <Link to="/privacy" className="tw-text-brand-gold hover:tw-underline">Privacy Policy</Link>.
            </p>
            <p className="tw-mb-0">
              Outstanding orders and financial obligations survive account termination.
            </p>
          </Section>

          {/* 12. Limitation of Liability */}
          <Section icon="exclamation-triangle" title="12. Limitation of Liability">
            <p className="tw-mb-2">
              Primal Marketplace is a platform that connects buyers and sellers. We are not a party to transactions between users and do not guarantee the quality, safety, or legality of items listed.
            </p>
            <p className="tw-mb-2">
              THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. TO THE MAXIMUM EXTENT PERMITTED BY LAW, PRIMAL MARKETPLACE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES.
            </p>
            <p className="tw-mb-0">
              Our total liability for any claim arising from your use of the Platform shall not exceed the amount of fees you have paid to us in the twelve (12) months preceding the claim.
            </p>
          </Section>

          {/* 13. Governing Law */}
          <Section icon="gavel" title="13. Governing Law">
            <p className="tw-mb-0">
              These Terms are governed by the laws of the United States. Any disputes arising from these Terms or your use of the Platform shall be resolved through binding arbitration in accordance with applicable rules, unless otherwise required by law.
            </p>
          </Section>

          {/* 14. Changes to Terms */}
          <Section icon="pencil" title="14. Changes to Terms">
            <p className="tw-mb-0">
              We may revise these Terms at any time. Material changes will be communicated via email or a prominent notice on the Platform at least 30 days before taking effect. Your continued use after the effective date constitutes acceptance.
            </p>
          </Section>

          {/* 14. Contact */}
          <Section icon="envelope" title="15. Contact">
            <p className="tw-mb-2">
              If you have questions about these Terms of Service, please contact us:
            </p>
            <p className="tw-mb-0">
              <strong className="tw-text-[#1a1a1a] dark:tw-text-white">Email:</strong>{' '}
              <a href="mailto:dattebayolabs@gmail.com" className="tw-text-brand-gold hover:tw-underline">dattebayolabs@gmail.com</a>
            </p>
          </Section>

          {/* Bottom nav */}
          <div className="tos-hidden tw-text-center tw-mt-8" data-anim="tos-fade-up">
            <Link to="/privacy" className="tw-text-brand-gold hover:tw-underline tw-text-sm tw-font-medium">
              Read our Privacy Policy &rarr;
            </Link>
          </div>

        </div>

        {/* Scroll to top button */}
        {this.state.showScrollTop && (
          <button
            onClick={function () { window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="tw-fixed tw-bottom-6 tw-right-6 tw-w-10 tw-h-10 tw-rounded-full tw-bg-brand-gold tw-text-black tw-border-0 tw-cursor-pointer tw-z-40 tw-flex tw-items-center tw-justify-center mobile-tab-offset-btn"
            style={{ boxShadow: '0 4px 16px rgba(201, 168, 76, 0.3)', animation: 'hp-fadeUp 0.3s ease forwards' }}
            aria-label="Scroll to top"
          >
            <svg className="tw-w-6 tw-h-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>
    );
  }
}

export default TermsOfService;
